import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API, { UPLOADS_URL } from '../api/axios';
import SEO from '../components/SEO';

const JobApplicationForm = () => {
  const title = 'Job Application Form';
  const [bannerImage, setBannerImage] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', qualification: '', experience: '', address: '', contact_no: '', resume: null });
  const [resumeName, setResumeName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    API.get('/pages/public/job-application-form').then(res => {
      if (res.data?.data?.banner_image) setBannerImage(res.data.data.banner_image);
    }).catch(() => {});
    document.title = title;
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setResumeName(file.name);
    setForm({ ...form, resume: file });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('email', form.email);
      fd.append('qualification', form.qualification);
      fd.append('experience', form.experience);
      fd.append('address', form.address);
      fd.append('contact_no', form.contact_no);
      if (form.resume) fd.append('resume', form.resume);
      await API.post('/enquiries/public/job-application', fd, { headers: { 'Content-Type': undefined } });
      setSubmitted(true);
      setForm({ name: '', email: '', qualification: '', experience: '', address: '', contact_no: '', resume: null });
      setResumeName('');
    } catch (err) {
      alert('Error submitting application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO slug="job-application-form" title={title} description="Apply for a job at St. Andrews World School, Siddharth Vihar. Submit your application with qualification, experience, and contact details." />

      <section className="page-hero">
        <img src={bannerImage ? `${UPLOADS_URL}/${bannerImage}` : `${process.env.PUBLIC_URL}/images/page-banners/job-application.jpg`} alt={title} className="page-hero-img" loading="eager" />
        <div className="page-hero-content">
          <div className="container">
            <div className="page-hero-inner"></div>
          </div>
        </div>
      </section>

      <section className="content-page">
        <div className="container">
          <div className="card p-7 md:p-10 max-w-[800px] mx-auto">
            <h2 className="text-[28px] text-primary mb-4">Job Application Form</h2>
            <div className="mb-6 space-y-2 text-body text-sm">
              <p><i className="fas fa-map-marker-alt text-secondary mr-2"></i>P.S.-01, Sector-09, Siddharth Vihar Ghaziabad – 201009</p>
              <p><i className="fas fa-phone text-secondary mr-2"></i><a href="tel:+919311668863">+91-9311668863</a>, <a href="tel:+919311668864">+91-9311668864</a></p>
              <p><i className="fas fa-envelope text-secondary mr-2"></i><a href="mailto:Info@sawssiddharthvihar.in">Info@sawssiddharthvihar.in</a></p>
            </div>

            {submitted ? (
              <div className="text-center p-10 bg-light rounded-md">
                <i className="fas fa-check-circle text-[48px] text-green-500 mb-4"></i>
                <h3 className="text-primary">Application Submitted!</h3>
                <p className="text-body text-center">Thank you for your interest. We will get back to you soon.</p>
                <button className="btn mt-4" onClick={() => setSubmitted(false)}>Submit Another Application</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="form-group">
                  <label>Name of the Candidate *</label>
                  <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required placeholder="Enter your full name"
                    className="w-full px-4 py-3 border border-gray-200 rounded-[8px] text-sm outline-none focus:border-secondary" />
                </div>
                <div className="form-group">
                  <label>Your email *</label>
                  <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required placeholder="Enter your email"
                    className="w-full px-4 py-3 border border-gray-200 rounded-[8px] text-sm outline-none focus:border-secondary" />
                </div>
                <div className="form-group">
                  <label>Qualification *</label>
                  <input type="text" value={form.qualification} onChange={e => setForm({...form, qualification: e.target.value})} required placeholder="Enter your qualification"
                    className="w-full px-4 py-3 border border-gray-200 rounded-[8px] text-sm outline-none focus:border-secondary" />
                </div>
                <div className="form-group">
                  <label>Experience *</label>
                  <input type="text" value={form.experience} onChange={e => setForm({...form, experience: e.target.value})} required placeholder="Years of experience"
                    className="w-full px-4 py-3 border border-gray-200 rounded-[8px] text-sm outline-none focus:border-secondary" />
                </div>
                <div className="form-group">
                  <label>Address *</label>
                  <textarea value={form.address} onChange={e => setForm({...form, address: e.target.value})} required placeholder="Enter your address"
                    className="w-full px-4 py-3 border border-gray-200 rounded-[8px] text-sm outline-none focus:border-secondary resize-y" />
                </div>
                <div className="form-group">
                  <label>Contact No. *</label>
                  <input type="tel" value={form.contact_no} onChange={e => setForm({...form, contact_no: e.target.value})} required placeholder="Enter your contact number"
                    className="w-full px-4 py-3 border border-gray-200 rounded-[8px] text-sm outline-none focus:border-secondary" />
                </div>
                <div className="form-group">
                  <label>Resume / CV *</label>
                  <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} className="w-full text-sm" />
                  <p className="text-xs text-muted mt-1">Upload your resume (PDF or DOC, max 5MB).</p>
                </div>
                <button type="submit" disabled={loading} className="btn">{loading ? 'Submitting...' : 'Submit Application'}</button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default JobApplicationForm;
